import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { RefreshCw, X, Sparkles, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface DemoModeBannerProps {
  onStartTour: () => void;
}

const DemoModeBanner = ({ onStartTour }: DemoModeBannerProps) => {
  const [isDemoUser, setIsDemoUser] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const checkDemoUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user?.email === "demo@bailbuddies.app") {
        setIsDemoUser(true);
        const tourShown = sessionStorage.getItem("demoTourShown");
        if (!tourShown) {
          setTimeout(() => onStartTour(), 1500);
          sessionStorage.setItem("demoTourShown", "true");
        }
      }
    };
    checkDemoUser();
  }, [onStartTour]);

  const handleResetData = async () => {
    setIsResetting(true);
    try {
      const { error } = await supabase.functions.invoke("create-demo-account");
      if (error) throw error;
      toast.success("Demo data reset! Refreshing...");
      setTimeout(() => window.location.reload(), 1500);
    } catch (error) {
      toast.error("Failed to reset demo data");
    } finally {
      setIsResetting(false);
    }
  };

  if (!isDemoUser || dismissed) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: -100, opacity: 0 }}
        className="demo-banner fixed top-0 left-0 right-0 z-50 bg-primary text-white shadow-lg"
      >
        <div className="container mx-auto px-4 py-2">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4" />
              <span className="font-display font-bold text-xs sm:text-sm">🎭 Demo Mode</span>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="secondary" onClick={onStartTour} className="bg-white/20 hover:bg-white/30 text-white border-0 text-xs h-7 px-2">
                <Play className="w-3 h-3 mr-1" /> Tour
              </Button>
              <Button size="sm" variant="secondary" onClick={handleResetData} disabled={isResetting} className="bg-white/20 hover:bg-white/30 text-white border-0 text-xs h-7 px-2">
                <RefreshCw className={`w-3 h-3 mr-1 ${isResetting ? "animate-spin" : ""}`} /> Reset
              </Button>
              <button onClick={() => setDismissed(true)} className="p-1 hover:bg-white/20 rounded-full"><X className="w-4 h-4" /></button>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default DemoModeBanner;
